interface FeishuConfig {
    appId: string;
    appSecret: string;
    baseUrl?: string;
}

interface FeishuRecord {
    record_id: string;
    fields: Record<string, any>;
}

export class FeishuService {
    private appId: string;
    private appSecret: string;
    private baseUrl: string;
    private tokenCache: { token: string; expireAt: number } | null = null;
    private appToken: string | undefined;
    private tableId: string | undefined;

    constructor(config?: FeishuConfig) {
        // 安全获取环境变量
        const getEnv = (key: string): string | undefined => {
            try {
                if (typeof import.meta !== 'undefined' && import.meta.env) {
                    const value = import.meta.env[key];
                    return typeof value === 'string' ? value : undefined;
                }
            } catch {
                // 忽略错误
            }
            if (typeof window !== 'undefined' && (window as any).__ENV__) {
                return (window as any).__ENV__[key];
            }
            return undefined;
        };
        
        // 优先使用传入的配置，否则使用环境变量
        this.appId = config?.appId || getEnv('VITE_FEISHU_APP_ID') || '';
        this.appSecret = config?.appSecret || getEnv('VITE_FEISHU_APP_SECRET') || '';
        this.appToken = getEnv('VITE_FEISHU_BITABLE_APP_TOKEN');
        this.tableId = getEnv('VITE_FEISHU_BITABLE_TABLE_ID');

        // 开发环境使用代理路径 /api/feishu，生产环境如果不配置 proxy 可能需要直连或云函数转发
        // 这里默认使用 Vite 配置的 /api/feishu 代理
        this.baseUrl = config?.baseUrl || '/api/feishu';
    }

    /**
     * 获取 tenant_access_token
     * 优先从内存缓存获取，如果过期则重新请求
     */
    async getAccessToken(): Promise<string> {
        const now = Date.now();
        if (this.tokenCache && this.tokenCache.expireAt > now) {
            return this.tokenCache.token;
        }

        if (!this.appId || !this.appSecret) {
            throw new Error('Feishu AppID or AppSecret is not configured');
        }

        try {
            const response = await fetch(`${this.baseUrl}/open-apis/auth/v3/tenant_access_token/internal`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify({
                    app_id: this.appId,
                    app_secret: this.appSecret
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch access token: ${response.statusText}`);
            }

            const data = await response.json();
            if (data.code !== 0) {
                throw new Error(`Feishu API Error: ${data.msg}`);
            }

            // 缓存 token，提取过期时间 (expire - 60s buffer)
            this.tokenCache = {
                token: data.tenant_access_token,
                expireAt: now + (data.expire * 1000) - 60000
            };

            return data.tenant_access_token;
        } catch (error) {
            console.error('Error fetching/refreshing Feishu token:', error);
            throw error;
        }
    }

    /**
     * 获取多维表格记录 (支持分页)
     * @param appToken 多维表格 App Token (可选，默认使用环境变量)
     * @param tableId 数据表 ID (可选，默认使用环境变量)
     */
    async getBitableRecords(appToken?: string, tableId?: string): Promise<FeishuRecord[]> {
        const targetAppToken = appToken || this.appToken;
        const targetTableId = tableId || this.tableId;

        if (!targetAppToken || !targetTableId) {
            throw new Error('App Token or Table ID is missing');
        }

        const records: FeishuRecord[] = [];
        let pageToken: string | undefined;

        try {
            do {
                const token = await this.getAccessToken();
                const url = new URL(`${window.location.origin}${this.baseUrl}/open-apis/bitable/v1/apps/${targetAppToken}/tables/${targetTableId}/records`);

                // 添加查询参数
                url.searchParams.append('page_size', '100'); // 飞书最大支持 100 ? Check docs, usually 20-100 is safe. Doc says default 20.
                if (pageToken) {
                    url.searchParams.append('page_token', pageToken);
                }

                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    // 处理 token失效的情况 (虽然有缓存，但万一服务端撤销了)
                    // 简单起见，这里直接报错，由上层决定是否重试（或清空缓存重试）
                    if (response.status === 401) {
                        this.tokenCache = null;
                        throw new Error('Unauthorized or Token expired during fetch');
                    }
                    throw new Error(`Failed to fetch records: ${response.statusText}`);
                }

                const data = await response.json();
                if (data.code !== 0) {
                    throw new Error(`Feishu API Error: ${data.msg}`);
                }

                if (data.data && data.data.items) {
                    records.push(...data.data.items);
                }

                pageToken = data.data?.page_token;
            } while (pageToken);

            return records;
        } catch (error) {
            console.error('Error fetching bitable records:', error);
            throw error;
        }
    }

    /**
     * 测试连接
     */
    async testConnection(appToken?: string, tableId?: string): Promise<boolean> {
        try {
            // 尝试获取 token
            const token = await this.getAccessToken();
            if (!token) return false;

            // 尝试读取一条记录来验证 table 权限
            const targetAppToken = appToken || this.appToken;
            const targetTableId = tableId || this.tableId;

            if (targetAppToken && targetTableId) {
                const url = `${this.baseUrl}/open-apis/bitable/v1/apps/${targetAppToken}/tables/${targetTableId}/records?page_size=1`;
                const response = await fetch(url, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                return data.code === 0;
            }
            return true;
        } catch (e) {
            console.error('Test connection failed:', e);
            return false;
        }
    }
}

export const feishuService = new FeishuService();
